import type { I18n } from '@learnforge/i18n';
import { applyTheme, onThemeMessage, resolveInitialTheme } from './theme';
import { mountThemeToggle } from './theme-toggle';
import { mountTopbar } from './topbar';
import { mountLangPicker } from './lang-picker';
import { mountRotateNudge } from './rotate-nudge';
import { mountMobileDrawer, type DrawerIcon } from './mobile-drawer';
import { mountHelp, type HelpHandle } from './help';
import { InfoBadge } from './info-badge';
import { getShotId, markShotMode } from './shot';
import {
  notifyHostExperiment,
  onExperimentRequest,
  readRequestedExperiment,
  writeExperimentUrl,
} from './experiment-url';
import type { ExperimentContext, ExperimentDef, MountedExperiment } from './experiment-shell';

declare global {
  interface Window {
    __demoReady?: Promise<void>;
    __reloadExperiment?: () => void;
  }
}

export interface BootstrapDemoShellOptions<
  KExp extends string,
  KMsg extends string,
  Ctx extends ExperimentContext<KMsg>,
> {
  /** Console-warning prefix, e.g. 'finance'. */
  slug: string;
  i18n: I18n<KMsg>;
  experiments: Readonly<Record<KExp, ExperimentDef<Ctx, KMsg>>>;
  order: readonly KExp[];
  /** Map ?shot=<id> to its experiment; return null for unknown ids. */
  shotToExperiment?: (shotId: string) => KExp | null;
  /** Extend the base context with demo-specific fields (e.g. prng). */
  extendContext: (base: ExperimentContext<KMsg>, info: { shotId: string | null }) => Ctx;
  drawerIcon?: DrawerIcon;
  eyebrowKey?: KMsg;
  selectorAriaLabelKey?: KMsg;
  /**
   * Experiment to land on when neither `?exp=` nor a resolvable `?shot=` is
   * present. Defaults to `order[0]`. Use this to pin a default selection that
   * differs from the topbar dropdown's displayed order — `order` itself still
   * governs the dropdown's option sequence unchanged.
   */
  defaultExperiment?: KExp;
  /**
   * Static array: mounted once at boot, unchanged for the shell's lifetime.
   * Function: called with the current experiment key on every `loadExperiment()`
   * (initial load, `reload()`, and selector switches) — the previous help panel is
   * destroyed and, when the function returns a non-empty array, remounted with the
   * keys for the newly-current experiment. Returning `undefined`/`[]` destroys
   * without remounting.
   */
  helpLineKeys?: readonly KMsg[] | ((exp: KExp | null) => readonly KMsg[] | undefined);
}

export interface DemoShellHandle<KExp extends string> {
  reload(): void;
  current(): KExp | null;
}

export function bootstrapDemoShell<
  KExp extends string,
  KMsg extends string,
  Ctx extends ExperimentContext<KMsg>,
>(opts: BootstrapDemoShellOptions<KExp, KMsg, Ctx>): DemoShellHandle<KExp> {
  applyTheme(resolveInitialTheme());
  mountThemeToggle();

  const isKey = (raw: string | null): raw is KExp => opts.order.includes(raw as KExp);
  const shotId = getShotId();
  const shotExperiment: KExp | null =
    shotId !== null && opts.shotToExperiment ? opts.shotToExperiment(shotId) : null;

  function initialExperiment(): KExp | null {
    if (opts.order.length === 0) return null;
    if (shotExperiment !== null) return shotExperiment;
    if (shotId !== null && opts.shotToExperiment) {
      console.warn(
        `[${opts.slug}] Unknown shot id "${shotId}" — falling back to first experiment.`,
      );
    }
    const raw = readRequestedExperiment(opts.slug);
    if (isKey(raw)) return raw;
    return opts.defaultExperiment ?? opts.order[0] ?? null;
  }

  let current: KExp | null = initialExperiment();
  let mounted: MountedExperiment | null = null;

  /** Pick up a `/<slug>/<key>` or `?exp=` change made since the last load; a shot pins the experiment. */
  function syncCurrentFromUrl(): void {
    if (shotExperiment !== null) return;
    const live = readRequestedExperiment(opts.slug);
    if (isKey(live)) current = live;
  }

  const stage = document.getElementById('stage') as HTMLDivElement;
  const paramsContainer = document.getElementById('paramsContainer') as HTMLDivElement;
  const actionBar = document.getElementById('actionBar') as HTMLDivElement;

  onThemeMessage((next) => {
    applyTheme(next);
    mounted?.onThemeChange?.(next);
  });
  onExperimentRequest(() => {
    if (current !== null) notifyHostExperiment(current);
  });
  opts.i18n.subscribe(() => {
    mounted?.onLanguageChange?.();
  });

  const infoBadgeEl = document.createElement('div');
  infoBadgeEl.id = 'infoBadge';
  document.body.appendChild(infoBadgeEl);
  const infoBadge = new InfoBadge(infoBadgeEl);

  mountTopbar(document.getElementById('topbar') as HTMLElement, {
    eyebrowKey: opts.eyebrowKey ?? ('panel.params' as KMsg),
    i18n: opts.i18n,
    ...(opts.order.length > 0
      ? {
          selector: {
            options: opts.order.map((key) => ({
              value: key,
              labelKey: opts.experiments[key].labelKey,
            })),
            value: current ?? opts.order[0]!,
            ariaLabelKey: opts.selectorAriaLabelKey ?? ('panel.experiment' as KMsg),
            onChange: (key: KExp) => {
              current = key;
              // Write before loading: loadExperiment() re-reads the URL first.
              writeExperimentUrl(opts.slug, current);
              loadExperiment();
            },
          },
        }
      : {}),
  });
  mountLangPicker(document.getElementById('langPicker') as HTMLElement, opts.i18n);
  mountRotateNudge();

  let helpHandle: HelpHandle | null = null;
  if (typeof opts.helpLineKeys !== 'function') {
    if (opts.helpLineKeys && opts.helpLineKeys.length > 0) {
      mountHelp({ lineKeys: [...opts.helpLineKeys], i18n: opts.i18n });
    }
  }

  /** Re-mount the per-experiment help panel. No-op unless `helpLineKeys` is a function. */
  function updateDynamicHelp(expKey: KExp | null): void {
    if (typeof opts.helpLineKeys !== 'function') return;
    helpHandle?.destroy();
    helpHandle = null;
    const keys = opts.helpLineKeys(expKey);
    if (keys && keys.length > 0) {
      helpHandle = mountHelp({ lineKeys: [...keys], i18n: opts.i18n });
    }
  }

  const paramsPanel = document.querySelector<HTMLElement>('.params-panel');
  if (paramsPanel) {
    mountMobileDrawer(paramsPanel, { side: 'left', icon: opts.drawerIcon ?? 'controls' });
  }

  function loadExperiment(): void {
    syncCurrentFromUrl();
    mounted?.dispose();
    mounted = null;
    stage.replaceChildren();
    paramsContainer.replaceChildren();
    actionBar.replaceChildren();

    if (current === null || !opts.experiments[current]) {
      stage.innerHTML =
        '<svg viewBox="0 0 800 460" aria-busy="false" style="width:100%;height:100%"><text x="400" y="230" text-anchor="middle" font-family="Space Grotesk, sans-serif">No experiments loaded yet — check back soon.</text></svg>';
      updateDynamicHelp(null);
      return;
    }

    const base: ExperimentContext<KMsg> = {
      root: stage,
      panel: paramsContainer,
      actionBar,
      i18n: opts.i18n,
      infoBadge,
    };
    mounted = opts.experiments[current].mount(opts.extendContext(base, { shotId }));
    updateDynamicHelp(current);
    // The URL always names what is on screen (a bare URL gains its segment).
    writeExperimentUrl(opts.slug, current);
    notifyHostExperiment(current);
  }

  loadExperiment();
  window.__reloadExperiment = loadExperiment;

  function twoPaints(): Promise<void> {
    return new Promise<void>((res) => {
      requestAnimationFrame(() => requestAnimationFrame(() => res()));
    });
  }

  if (shotId !== null) {
    markShotMode();
    const harnessReady = (mounted as { ready?: Promise<void> } | null)?.ready;
    window.__demoReady = harnessReady ? harnessReady.then(() => twoPaints()) : twoPaints();
  } else {
    window.__demoReady = twoPaints();
  }

  return { reload: loadExperiment, current: () => current };
}
